// src/modals/FiltersModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../hooks';
import PajNtaubBand from '../components/ui/PajNtaubBand';
import { FAMILLES, INTENTIONS, GENDERS } from '../constants';
import { Btn, Input, Select } from '../components/ui';
import { searchCities } from '../utils/geocoding';

const FiltersModal = ({ isOpen, onClose, filters, setFilters, onApply, onReset, userClan }) => {
  const { onBackdrop } = useModal(isOpen, onClose);
  const { t } = useTranslation();
  const [cityQuery, setCityQuery] = useState(filters.city || '');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDebounceRef = useRef(null);
  const citySelectedRef = useRef(!!filters.city);

  // Sync cityQuery quand la modale s'ouvre ou filters.city change (ex: reset)
  useEffect(() => {
    if (isOpen) {
      setCityQuery(filters.city || '');
      citySelectedRef.current = !!filters.city;
      setCitySuggestions([]);
      setShowCityDropdown(false);
    }
  }, [isOpen, filters.city]);

  // Autocomplete ville (debounce 300ms)
  useEffect(() => {
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    if (cityQuery.length < 3 || citySelectedRef.current) {
      setCitySuggestions([]);
      return;
    }
    cityDebounceRef.current = setTimeout(async () => {
      const results = await searchCities(cityQuery);
      setCitySuggestions(results);
      setShowCityDropdown(results.length > 0);
    }, 300);
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    };
  }, [cityQuery]);

  const selectCity = (loc) => {
    setCityQuery(loc.city);
    setShowCityDropdown(false);
    setCitySuggestions([]);
    citySelectedRef.current = true;
    setFilters({ ...filters, city: loc.city });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="filters-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('filtersModal.title')}
        className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl max-h-[85dvh] overflow-hidden animate-slide-up"
      >
        <PajNtaubBand height={4} />
        <div className="flex justify-between items-center p-4 sm:p-5 border-b dark:border-gray-800">
          <h2 className="text-base font-semibold dark:text-gray-100">{t('filtersModal.title')}</h2>
          <button
            onClick={onClose}
            aria-label={t('filtersModal.ariaClose')}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
          >
            <X size={20} className="dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-5 overflow-y-auto max-h-[60vh] space-y-5">
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
              {t('filtersModal.ageLabel')}
            </label>
            <div className="flex gap-3 items-center">
              <Input
                type="number"
                placeholder="Min"
                min="15"
                max="99"
                value={filters.ageMin}
                onChange={(e) => setFilters({ ...filters, ageMin: e.target.value })}
                onBlur={(e) => {
                  let v = Number(e.target.value);
                  if (!e.target.value) return;
                  if (v < 15) v = 15;
                  if (v > 99) v = 99;
                  const max = Number(filters.ageMax);
                  if (filters.ageMax && v > max) {
                    setFilters({ ...filters, ageMin: String(v), ageMax: String(v) });
                  } else {
                    setFilters({ ...filters, ageMin: String(v) });
                  }
                }}
              />
              <span className="text-gray-400">{t('filtersModal.ageTo')}</span>
              <Input
                type="number"
                placeholder="Max"
                min="15"
                max="99"
                value={filters.ageMax}
                onChange={(e) => setFilters({ ...filters, ageMax: e.target.value })}
                onBlur={(e) => {
                  let v = Number(e.target.value);
                  if (!e.target.value) return;
                  if (v < 15) v = 15;
                  if (v > 99) v = 99;
                  const min = Number(filters.ageMin);
                  if (filters.ageMin && v < min) {
                    setFilters({ ...filters, ageMax: String(v), ageMin: String(v) });
                  } else {
                    setFilters({ ...filters, ageMax: String(v) });
                  }
                }}
              />
            </div>
          </div>

          <Select
            label={t('filtersModal.genderLabel')}
            value={filters.gender}
            onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
            options={GENDERS.map((g) => ({ value: g.value, label: t(g.labelKey) }))}
            placeholder={t('filtersModal.genderAll')}
          />

          {/* Ville — autocomplete Nominatim */}
          <div className="relative space-y-1.5">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('filtersModal.cityLabel')}
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <MapPin size={16} />
              </div>
              <input
                type="text"
                value={cityQuery}
                onChange={(e) => {
                  setCityQuery(e.target.value);
                  citySelectedRef.current = false;
                  if (filters.city) {
                    setFilters({ ...filters, city: '' });
                  }
                }}
                placeholder={t('filtersModal.cityPlaceholder')}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition"
                data-testid="filters-city-input"
              />
            </div>
            {showCityDropdown && citySuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                {citySuggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => selectCity(s)}
                    className="w-full px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-gray-800 transition flex items-center gap-2 text-sm dark:text-gray-200"
                    data-testid="filters-city-suggestion"
                  >
                    <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            )}
            {filters.city && (
              <div className="mt-1 p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center gap-2">
                <MapPin
                  size={14}
                  className="text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  {filters.city}
                </span>
              </div>
            )}
          </div>

          <Select
            label={t('filtersModal.clanLabel')}
            value={filters.clan}
            onChange={(e) => setFilters({ ...filters, clan: e.target.value })}
            options={FAMILLES.filter((c) => c !== userClan).map((c) => ({
              value: c,
              label: c === 'Autre' ? t('constants.familles.autre') : c,
            }))}
            placeholder={t('filtersModal.clanAll')}
          />
          <Select
            label={t('filtersModal.intentionLabel')}
            value={filters.intention}
            onChange={(e) => setFilters({ ...filters, intention: e.target.value })}
            options={INTENTIONS.map((i) => ({ value: i.value, label: t(i.labelKey) }))}
            placeholder={t('filtersModal.intentionAll')}
          />

          {/* Distance */}
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
              {t('filtersModal.distanceLabel')}
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.noDistanceLimit}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      noDistanceLimit: e.target.checked,
                      distance: e.target.checked ? '' : filters.distance,
                    })
                  }
                  className="rounded"
                />
                {t('filtersModal.noDistanceLimit')}
              </label>
              {!filters.noDistanceLimit && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="5"
                    max="500"
                    value={filters.distance}
                    onChange={(e) => setFilters({ ...filters, distance: e.target.value })}
                    placeholder="100"
                  />
                  <span className="text-sm text-gray-400">km</span>
                </div>
              )}
            </div>
          </div>

          {/* Intentions multi-select */}
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
              {t('filtersModal.intentionsLabel')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {INTENTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    const current = filters.intentions || [];
                    setFilters({
                      ...filters,
                      intentions: current.includes(opt.value)
                        ? current.filter((v) => v !== opt.value)
                        : [...current, opt.value],
                    });
                  }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                    (filters.intentions || []).includes(opt.value)
                      ? 'bg-rose-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
            {(!filters.intentions || filters.intentions.length === 0) && (
              <p className="text-xs text-gray-400 mt-1">{t('filtersModal.allIntentions')}</p>
            )}
          </div>

          <div className="space-y-4">
            {[
              { k: 'verifiedOnly', lk: 'filtersModal.verifiedOnly' },
              { k: 'onlineOnly', lk: 'filtersModal.onlineOnly' },
            ].map((tog) => (
              <label key={tog.k} className="flex justify-between items-center cursor-pointer">
                <span className="font-medium text-sm dark:text-gray-300">{t(tog.lk)}</span>
                <div
                  onClick={() => setFilters({ ...filters, [tog.k]: !filters[tog.k] })}
                  className={`w-12 h-7 rounded-full flex items-center px-1 cursor-pointer transition ${filters[tog.k] ? 'bg-rose-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                >
                  <span
                    className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${filters[tog.k] ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-5 border-t dark:border-gray-800 flex gap-3">
          <Btn
            v="ghost"
            onClick={onReset}
            className="flex-1 text-sm py-2.5"
            data-testid="filters-btn-reset"
          >
            {t('filtersModal.resetBtn')}
          </Btn>
          <Btn onClick={onApply} className="flex-1 text-sm py-2.5" data-testid="filters-btn-apply">
            {t('filtersModal.applyBtn')}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default FiltersModal;
